// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  useBooleanState,
  useHasMounted,
  usePrefersReducedMotion,
} from "@outschool/ui-utils";
import React from "react";
import ReactDomConfetti, { ConfettiConfig } from "react-dom-confetti";

export interface ConfettiProps {
  /** Triggers an explosion when the prop transitions from falsy to truthy. */
  active: boolean;
  /** An array of color values, defaults to some Outschool colors: ["#2ba7ce",
   * "#255dad", "#053873", "#fdc840", "#29a877", "#4267b2", "#c74c5a"]. */
  colors?: ConfettiConfig["colors"];
  /** Direction of the explosion in degrees, defaults to 90. */
  angle?: ConfettiConfig["angle"];
  /** Spread of the explosion in degrees, defaults to 45. */
  spread?: ConfettiConfig["spread"];
  /** Initial velocity of the particles, defaults to 45. */
  startVelocity?: ConfettiConfig["startVelocity"];
  /** Number of particle elements, defaults to 50. */
  elementCount?: ConfettiConfig["elementCount"];
  /** Decrease in velocity proportional to current velocity, default to 0.1. */
  dragFriction?: ConfettiConfig["dragFriction"];
  /** Delay for each fetti in milliseconds, defaults to 0. */
  stagger?: ConfettiConfig["stagger"];
}

/**
 * Celebrate good times! Add a bit of fun to any page. The confetti only
 * triggers if the state transitions `active` from `false` to `true`.
 *
 * See also the `useConfetti` hook.
 */
const Confetti: React.FC<ConfettiProps> = ({
  active,
  angle = 90,
  spread = 45,
  startVelocity = 45,
  elementCount = 50,
  dragFriction = 0.1,
  stagger = 0,
  colors = [
    "#2ba7ce",
    "#255dad",
    "#053873",
    "#fdc840",
    "#29a877",
    "#4267b2",
    "#c74c5a",
  ],
  ...rest
}) => {
  const config: ConfettiConfig = {
    angle,
    spread,
    startVelocity,
    elementCount,
    dragFriction,
    stagger,
    colors,
    ...rest,
  };

  const hasMounted = useHasMounted();
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!hasMounted || prefersReducedMotion) {
    return null;
  }

  return <ReactDomConfetti active={active} config={config} />;
};

export const useConfetti = (config?: ConfettiConfig) => {
  const [active, setActive, clearActive] = useBooleanState();
  React.useEffect(() => {
    if (active) {
      clearActive();
    }
  }, [active, clearActive]);
  return {
    confettiCannon: <Confetti active={active} {...config} />,
    fireConfetti: setActive,
  };
};
